import { FunctionComponent, useCallback, useState } from 'react'
import { OurIntlShape } from '@/i18n/typing'
import { exhaustiveCheck } from '@/utils/exhaustive-check'
import AnalysisSlideComponent from '@/utils/export-pptx/components/slides/slide-analysis'
import CommentBoardSlideComponent from '@/utils/export-pptx/components/slides/slide-comment-board'
import CommentSentimentSlideComponent from '@/utils/export-pptx/components/slides/slide-comment-sentiment'
import CommentSentimentRankingSlideComponent from '@/utils/export-pptx/components/slides/slide-comment-sentiment-ranking'
import CommentSentimentRatioSlideComponent from '@/utils/export-pptx/components/slides/slide-comment-sentiment-ratio'
import CommentWordCloudSlideComponent from '@/utils/export-pptx/components/slides/slide-comment-word-cloud'
import ContactSlideComponent from '@/utils/export-pptx/components/slides/slide-contact'
import CooperationContentSlideComponent from '@/utils/export-pptx/components/slides/slide-cooperation-content'
import HomeSlideComponent from '@/utils/export-pptx/components/slides/slide-home'
import InsightOverviewSlideComponent from '@/utils/export-pptx/components/slides/slide-insight-overview'
import KdSlideComponent from '@/utils/export-pptx/components/slides/slide-kd'
import KolCustomizedTagFollowerLevelSlideComponent from '@/utils/export-pptx/components/slides/slide-kol-customized-tag-follower-level'
import KolEngagementViewSlideComponent from '@/utils/export-pptx/components/slides/slide-kol-engagement-view'
import KolInfoSlideComponent from '@/utils/export-pptx/components/slides/slide-kol-info'
import KolLevelSlideComponent from '@/utils/export-pptx/components/slides/slide-kol-level'
import KolPerformanceSlideComponent from '@/utils/export-pptx/components/slides/slide-kol-performance'
import KolPerformanceStatsSlideComponent from '@/utils/export-pptx/components/slides/slide-kol-performance-stats'
import KolPostRankingSlideComponent from '@/utils/export-pptx/components/slides/slide-kol-post-ranking'
import KolPostWallSlideComponent from '@/utils/export-pptx/components/slides/slide-kol-post-wall'
import OutlineSlideComponent from '@/utils/export-pptx/components/slides/slide-outline'
import PlatformAnalysisSlideComponent from '@/utils/export-pptx/components/slides/slide-platform-analysis'
import PlatformKolListSlideComponent from '@/utils/export-pptx/components/slides/slide-platform-kol-list'
import Slide from '@/utils/export-pptx/types/slide'
import { toVoidFunction } from '@/utils/to-void-function'

const Pptx: FunctionComponent<{
  slides: Slide[]
  onSlideReady: (slide: Slide) => Promise<void>
  onPptxReady: () => Promise<void>
  formatMessage: OurIntlShape['formatMessage']
  formatNumber: OurIntlShape['formatNumber']
}> = ({ slides, onPptxReady, onSlideReady, formatMessage, formatNumber }) => {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0)

  const handleSlideReady = useCallback(
    async (slide: Slide): Promise<void> => {
      await onSlideReady(slide)
      setCurrentSlideIndex((i) => i + 1)

      if (currentSlideIndex === slides.length - 1) {
        await onPptxReady()
      }
    },
    [currentSlideIndex, onPptxReady, onSlideReady, slides.length],
  )

  const currentSlide = slides[currentSlideIndex]

  if (!currentSlide) {
    return null
  }

  if (currentSlide.type === 'kd') {
    return (
      <KdSlideComponent
        formatMessage={formatMessage}
        key={currentSlideIndex}
        slide={currentSlide}
        onSlideReady={toVoidFunction(handleSlideReady)}
      />
    )
  }

  if (currentSlide.type === 'home') {
    return (
      <HomeSlideComponent
        formatMessage={formatMessage}
        key={currentSlideIndex}
        slide={currentSlide}
        onSlideReady={toVoidFunction(handleSlideReady)}
      />
    )
  }

  if (currentSlide.type === 'outline') {
    return (
      <OutlineSlideComponent
        formatMessage={formatMessage}
        key={currentSlideIndex}
        slide={currentSlide}
        onSlideReady={toVoidFunction(handleSlideReady)}
      />
    )
  }

  if (currentSlide.type === 'cooperation-content') {
    return (
      <CooperationContentSlideComponent
        formatMessage={formatMessage}
        key={currentSlideIndex}
        slide={currentSlide}
        onSlideReady={toVoidFunction(handleSlideReady)}
      />
    )
  }

  if (currentSlide.type === 'analysis') {
    return (
      <AnalysisSlideComponent
        formatMessage={formatMessage}
        key={currentSlideIndex}
        slide={currentSlide}
        onSlideReady={toVoidFunction(handleSlideReady)}
      />
    )
  }

  if (currentSlide.type === 'platform-analysis') {
    return (
      <PlatformAnalysisSlideComponent
        formatMessage={formatMessage}
        key={currentSlideIndex}
        slide={currentSlide}
        onSlideReady={toVoidFunction(handleSlideReady)}
      />
    )
  }

  if (currentSlide.type === 'platform-kol-list') {
    return (
      <PlatformKolListSlideComponent
        formatMessage={formatMessage}
        key={currentSlideIndex}
        slide={currentSlide}
        onSlideReady={toVoidFunction(handleSlideReady)}
      />
    )
  }

  if (currentSlide.type === 'kol-level') {
    return (
      <KolLevelSlideComponent
        formatMessage={formatMessage}
        key={currentSlideIndex}
        slide={currentSlide}
        onSlideReady={toVoidFunction(handleSlideReady)}
      />
    )
  }

  if (currentSlide.type === 'kol-info') {
    return (
      <KolInfoSlideComponent
        formatMessage={formatMessage}
        key={currentSlideIndex}
        slide={currentSlide}
        onSlideReady={toVoidFunction(handleSlideReady)}
      />
    )
  }

  if (currentSlide.type === 'contact') {
    return (
      <ContactSlideComponent
        formatMessage={formatMessage}
        key={currentSlideIndex}
        slide={currentSlide}
        onSlideReady={toVoidFunction(handleSlideReady)}
      />
    )
  }

  if (currentSlide.type === 'insight-overview') {
    return (
      <InsightOverviewSlideComponent
        formatMessage={formatMessage}
        key={currentSlideIndex}
        slide={currentSlide}
        onSlideReady={toVoidFunction(handleSlideReady)}
      />
    )
  }

  if (currentSlide.type === 'kol-performance') {
    return (
      <KolPerformanceSlideComponent
        formatMessage={formatMessage}
        key={currentSlideIndex}
        slide={currentSlide}
        onSlideReady={toVoidFunction(handleSlideReady)}
      />
    )
  }

  if (currentSlide.type === 'kol-post-ranking') {
    return (
      <KolPostRankingSlideComponent
        formatMessage={formatMessage}
        formatNumber={formatNumber}
        key={currentSlideIndex}
        slide={currentSlide}
        onSlideReady={toVoidFunction(handleSlideReady)}
      />
    )
  }

  if (currentSlide.type === 'kol-customized-tag-follower-level') {
    return (
      <KolCustomizedTagFollowerLevelSlideComponent
        formatMessage={formatMessage}
        formatNumber={formatNumber}
        key={currentSlideIndex}
        slide={currentSlide}
        onSlideReady={toVoidFunction(handleSlideReady)}
      />
    )
  }

  if (currentSlide.type === 'kol-engagement-view') {
    return (
      <KolEngagementViewSlideComponent
        formatMessage={formatMessage}
        key={currentSlideIndex}
        slide={currentSlide}
        onSlideReady={toVoidFunction(handleSlideReady)}
      />
    )
  }

  if (currentSlide.type === 'comment-sentiment') {
    return (
      <CommentSentimentSlideComponent
        formatMessage={formatMessage}
        key={currentSlideIndex}
        slide={currentSlide}
        onSlideReady={toVoidFunction(handleSlideReady)}
      />
    )
  }

  if (currentSlide.type === 'comment-sentiment-ratio') {
    return (
      <CommentSentimentRatioSlideComponent
        formatMessage={formatMessage}
        key={currentSlideIndex}
        slide={currentSlide}
        onSlideReady={toVoidFunction(handleSlideReady)}
      />
    )
  }

  if (currentSlide.type === 'comment-sentiment-ranking') {
    return (
      <CommentSentimentRankingSlideComponent
        formatMessage={formatMessage}
        key={currentSlideIndex}
        slide={currentSlide}
        onSlideReady={toVoidFunction(handleSlideReady)}
      />
    )
  }

  if (currentSlide.type === 'comment-word-cloud') {
    return (
      <CommentWordCloudSlideComponent
        formatMessage={formatMessage}
        key={currentSlideIndex}
        slide={currentSlide}
        onSlideReady={toVoidFunction(handleSlideReady)}
      />
    )
  }

  if (currentSlide.type === 'comment-board') {
    return (
      <CommentBoardSlideComponent
        formatMessage={formatMessage}
        key={currentSlideIndex}
        slide={currentSlide}
        onSlideReady={toVoidFunction(handleSlideReady)}
      />
    )
  }

  if (currentSlide.type === 'kol-post-wall') {
    return (
      <KolPostWallSlideComponent
        formatMessage={formatMessage}
        key={currentSlideIndex}
        slide={currentSlide}
        onSlideReady={toVoidFunction(handleSlideReady)}
      />
    )
  }

  if (currentSlide.type === 'kol-performance-stats') {
    return (
      <KolPerformanceStatsSlideComponent
        formatMessage={formatMessage}
        key={currentSlideIndex}
        slide={currentSlide}
        onSlideReady={toVoidFunction(handleSlideReady)}
      />
    )
  }

  return exhaustiveCheck(currentSlide)
}

export default Pptx
